/**
 * Agent Settings Dialog Component
 */

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import { AgentNodeData } from '../types'

interface AgentSettingsDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  data: AgentNodeData
  onUpdateAgent: (updates: Partial<AgentNodeData>) => void
}

export function AgentSettingsDialog({
  open,
  onOpenChange,
  data,
  onUpdateAgent,
}: AgentSettingsDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Agent Settings</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium">Name</label>
              <Input
                value={data.name || ''}
                onChange={(e) => onUpdateAgent({ name: e.target.value })}
              />
            </div>
            <div>
              <label className="text-sm font-medium">Role</label>
              <Select
                value={data.role || 'general'}
                onValueChange={(value) => onUpdateAgent({ role: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="reviewer">Reviewer</SelectItem>
                  <SelectItem value="optimizer">Optimizer</SelectItem>
                  <SelectItem value="documentation_specialist">
                    Documentation Specialist
                  </SelectItem>
                  <SelectItem value="security_analyst">
                    Security Analyst
                  </SelectItem>
                  <SelectItem value="data_analyst">Data Analyst</SelectItem>
                  <SelectItem value="quality_assurance">
                    Quality Assurance
                  </SelectItem>
                  <SelectItem value="general">General</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <label className="text-sm font-medium">Instructions</label>
            <Textarea
              value={data.instructions || ''}
              onChange={(e) =>
                onUpdateAgent({ instructions: e.target.value })
              }
              placeholder="Provide specific instructions for this agent..."
              rows={4}
            />
          </div>

          <div>
            <label className="text-sm font-medium">Observation List</label>
            <div className="flex flex-wrap gap-1 mt-1">
              {data.observationList?.map((nodeId) => (
                <Badge key={nodeId} variant="secondary" className="text-xs">
                  {nodeId}
                  <button
                    onClick={() => {
                      const newList =
                        data.observationList?.filter((id) => id !== nodeId) ||
                        []
                      onUpdateAgent({ observationList: newList })
                    }}
                    className="ml-1 text-red-500"
                  >
                    ×
                  </button>
                </Badge>
              ))}
            </div>
            {(!data.observationList || data.observationList.length === 0) && (
              <p className="text-sm text-gray-500 mt-1">
                No nodes are being observed. Connect this agent to other nodes to enable observation.
              </p>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
